import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApprovalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type {
  ApprovalRequestDto,
  DecideApprovalRequestDto,
} from "@/features/admin/api/dto";

/**
 * Approuve une demande (`POST /ApprovalRequests/{id}/approve`).
 * Règle des quatre yeux (guide §5.4) : l'approbateur DOIT être un compte
 * différent du demandeur — une auto-approbation renvoie 422 (le message
 * backend remonte via `getBackendErrorMessage`). Rôles :
 * flashpay-superadmin, flashpay-admin.
 */
export const useApproveApprovalRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "approval-requests", "approve"],
    mutationFn: ({
      approvalRequestId,
      approverKeycloakId,
    }: {
      approvalRequestId: string;
      approverKeycloakId: string;
    }) =>
      mutateData({
        url: adminEndpoints.approvalRequests.approve(approvalRequestId),
        reqBody: {
          approverKeycloakId,
          rejectionReason: null,
        } satisfies DecideApprovalRequestDto,
        method: "POST",
      }) as Promise<ApprovalRequestDto>,
    onSuccess: (_data, { approvalRequestId }) => {
      return invalidateAfterMutation(
        queryClient,
        adminApprovalCacheKeys(approvalRequestId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useApproveApprovalRequest failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
